// Frontend deployment configuration
// This file manages deployment-specific configuration using environment variables

// Default configuration for development
const defaultConfig = {
  deployment: {
    domain: 'localhost',
    protocol: 'http',
    customDomains: ['localhost', '127.0.0.1', 'pixi-latte.local']
  },
  branding: {
    appName: 'gamimap',
    companyName: 'gamimap',
    tagline: 'Gamify Every Location',
    supportEmail: 'support@exploreandwin.com',
    copyrightYear: 2025
  },
  email: {
    webmail: 'https://mail.exploreandwin.com'
  },
  api: {
    baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:3001',
    version: 'v1',
    timeout: 30000
  },
  websocket: {
    enabled: false, // Disable WebSocket in production
    url: null
  },
  frontend: {
    publicUrl: process.env.PUBLIC_URL || '',
    googleMapsKey: null,
    googleAnalyticsId: null,
    cdnUrl: null
  },
  features: {
    enableNFT: true,
    enableLeaderboards: true,
    enableSocialSharing: true,
    enablePushNotifications: false,
    enableOfflineMode: false,
    maintenanceMode: false
  },
  social: {
    facebook: null,
    twitter: null,
    instagram: null,
    linkedin: null
  },
  legal: {
    privacyPolicyUrl: '/privacy',
    termsOfServiceUrl: '/terms',
    cookiePolicyUrl: '/cookies'
  }
};

// Get current environment
const getEnvironment = () => {
  return process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';
};

// Get configuration for current environment
const getConfig = () => {
  const environment = getEnvironment();
  const config = { ...defaultConfig };
  
  // Production overrides
  if (environment === 'production') {
    config.deployment.domain = 'exploreandwin.com';
    config.deployment.protocol = 'https';
    config.deployment.customDomains = ['exploreandwin.com', 'www.exploreandwin.com'];
    config.api.baseUrl = 'https://api.exploreandwin.com';
    config.frontend.publicUrl = 'https://www.exploreandwin.com';
    config.websocket.enabled = false; // Disable WebSocket in production
  } else {
    // Development overrides
    config.websocket.enabled = true;
    config.websocket.url = 'ws://localhost:3000/ws';
  }
  
  // Override with environment variables if present
  if (process.env.REACT_APP_API_URL) {
    config.api.baseUrl = process.env.REACT_APP_API_URL;
  }
  
  if (process.env.REACT_APP_APP_NAME) {
    config.branding.appName = process.env.REACT_APP_APP_NAME;
  }
  
  if (process.env.REACT_APP_COMPANY_NAME) {
    config.branding.companyName = process.env.REACT_APP_COMPANY_NAME;
  }
  
  return config;
};

// Export configuration
const config = getConfig();

export default {
  // Full configuration object
  config,
  
  // Deployment info
  domain: config.deployment.domain,
  protocol: config.deployment.protocol,
  environment: getEnvironment(),
  
  // Branding
  appName: config.branding.appName,
  companyName: config.branding.companyName,
  tagline: config.branding.tagline,
  supportEmail: config.branding.supportEmail,
  copyrightYear: config.branding.copyrightYear,
  
  // API configuration
  apiBaseUrl: config.api.baseUrl,
  apiVersion: config.api.version,
  apiTimeout: config.api.timeout,
  
  // WebSocket configuration
  websocketEnabled: config.websocket.enabled,
  websocketUrl: config.websocket.url,
  
  // Frontend configuration
  publicUrl: config.frontend.publicUrl,
  googleMapsKey: config.frontend.googleMapsKey || process.env.REACT_APP_GOOGLE_MAPS_KEY,
  googleAnalyticsId: config.frontend.googleAnalyticsId || process.env.REACT_APP_GA_ID,
  
  // Feature flags
  features: {
    nft: config.features.enableNFT,
    leaderboards: config.features.enableLeaderboards,
    socialSharing: config.features.enableSocialSharing,
    pushNotifications: config.features.enablePushNotifications,
    offlineMode: config.features.enableOfflineMode,
    maintenanceMode: config.features.maintenanceMode
  },
  
  // Social links
  social: config.social,
  
  // Legal links
  legal: config.legal,
  
  // Helper functions
  isFeatureEnabled: (feature) => {
    return config.features && config.features[`enable${feature.charAt(0).toUpperCase() + feature.slice(1)}`] === true;
  },
  
  isMaintenanceMode: () => {
    return config.features && config.features.maintenanceMode === true;
  },
  
  getApiUrl: (endpoint = '') => {
    const base = config.api.baseUrl.replace(/\/$/, '');
    const path = endpoint.replace(/^\//, '');
    return path ? `${base}/${path}` : base;
  },
  
  getAssetUrl: (path) => {
    const base = config.frontend.cdnUrl || config.frontend.publicUrl || '';
    return base ? `${base}/${path}` : path;
  }
};